/**
 * CollectorEmitter — the real analytics transport for production.
 *
 * Design (metrics-plan §5):
 *   - Batches events in memory; flushes to POST /e (our first-party collector).
 *   - The client NEVER talks to PostHog directly and holds no PostHog key.
 *   - Flush triggers: interval (~10 s), page hide (visibilitychange/sendBeacon),
 *     and when the queue reaches ~20 events.
 *   - Network failures drop silently; never throws; never blocks render.
 *   - Respects DNT: if dnt is true, enabled=false and nothing is ever sent.
 *
 * Batch format: POST /e with `Content-Type: application/json` body:
 *   { "events": [ …AnalyticsEvent[] ] }
 *
 * The /e collector re-validates each event via safeParseEvent before forwarding
 * (src/contracts/analytics.ts), so a malformed event is dropped at the boundary.
 */

import type { AnalyticsEmitter, AnalyticsEvent } from 'briefpane/contracts';
import { getIdentity } from './identity.js';

/** How many events to buffer before forcing a flush. */
const QUEUE_FLUSH_THRESHOLD = 20;

/** Flush interval in milliseconds. */
const FLUSH_INTERVAL_MS = 10_000;

/**
 * The analytics collector endpoint.
 *
 * Web build   (no VITE_API_BASE):  '/e'                              — same-origin ✓
 * Extension build (VITE_API_BASE=https://briefpane.com):
 *   'https://briefpane.com/e'                                        — absolute ✓
 *
 * VITE_API_BASE is the shared base for all server endpoints; the collector path
 * is always '/e', appended to the base. The extension's host_permissions include
 * 'https://briefpane.com/*', so this cross-origin POST is exempt from CORS
 * (same mechanism as the tile cache fetch — spike-confirmed).
 */
const COLLECTOR_ENDPOINT = (import.meta.env.VITE_API_BASE ?? '') + '/e';

export interface CollectorEmitterOptions {
  /** Override the flush interval (ms) — for testing. Default: 10 000. */
  readonly flushIntervalMs?: number;
  /** Override the batch size threshold. Default: 20. */
  readonly queueThreshold?: number;
  /** Override the endpoint. Default: '/e'. */
  readonly endpoint?: string;
  /**
   * Injectable fetch implementation — for testing without real network.
   * Defaults to globalThis.fetch.
   */
  readonly fetchFn?: typeof fetch;
  /**
   * Override the DNT flag — for testing. When not provided, reads from
   * getIdentity().dnt (which reads navigator.doNotTrack at module load time).
   */
  readonly dntOverride?: boolean;
}

class CollectorEmitter implements AnalyticsEmitter {
  readonly enabled: boolean;

  private readonly _queue: AnalyticsEvent[] = [];
  private _timer: ReturnType<typeof setInterval> | null = null;
  private _onVisibility: (() => void) | null = null;
  private _onPageHide: (() => void) | null = null;
  private readonly _flushIntervalMs: number;
  private readonly _queueThreshold: number;
  private readonly _endpoint: string;
  private readonly _fetchFn: typeof fetch;

  constructor(options: CollectorEmitterOptions = {}) {
    // Fail-closed on privacy: if DNT is set, disable entirely.
    // dntOverride allows tests to inject the DNT state without mocking the module singleton.
    const dnt = options.dntOverride !== undefined ? options.dntOverride : getIdentity().dnt;
    this.enabled = !dnt;

    this._flushIntervalMs = options.flushIntervalMs ?? FLUSH_INTERVAL_MS;
    this._queueThreshold = options.queueThreshold ?? QUEUE_FLUSH_THRESHOLD;
    this._endpoint = options.endpoint ?? COLLECTOR_ENDPOINT;
    this._fetchFn = options.fetchFn ?? globalThis.fetch.bind(globalThis);

    if (this.enabled) {
      this._startTimer();
      this._attachPageHide();
    }
  }

  emit(event: AnalyticsEvent): void {
    if (!this.enabled) return;
    // Immutable push: create a new queue entry rather than mutating.
    this._queue.push(event);
    if (this._queue.length >= this._queueThreshold) {
      void this._flush();
    }
  }

  /** Force a flush — exposed for testing. */
  flushNow(): Promise<void> {
    return this._flush();
  }

  /** Drain and post queued events. Returns immediately; errors are swallowed. */
  private async _flush(): Promise<void> {
    if (this._queue.length === 0) return;
    // Drain the queue atomically (splice returns the removed items).
    const batch = this._queue.splice(0, this._queue.length);
    try {
      await this._fetchFn(this._endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: batch }),
        // keepalive: true would be nice but is size-limited; sendBeacon handles page-hide.
      });
    } catch {
      // Network failure: drop silently. Analytics must never degrade the product.
    }
  }

  /** Flush synchronously via sendBeacon on page hide (most reliable pre-unload path). */
  private _flushBeacon(): void {
    if (!this.enabled || this._queue.length === 0) return;
    const batch = this._queue.splice(0, this._queue.length);
    try {
      const payload = JSON.stringify({ events: batch });
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(this._endpoint, payload);
      } else {
        // Fallback: fire-and-forget fetch (may not complete on unload, that's acceptable).
        void this._fetchFn(this._endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        }).catch(() => { /* swallow */ });
      }
    } catch {
      // Swallow all errors — analytics never degrades the product.
    }
  }

  private _startTimer(): void {
    this._timer = setInterval(() => {
      void this._flush();
    }, this._flushIntervalMs);
    // Do not prevent the process from exiting in Node environments.
    if (this._timer && typeof this._timer === 'object' && 'unref' in this._timer) {
      (this._timer as { unref(): void }).unref();
    }
  }

  private _attachPageHide(): void {
    // Feature-detect the functions, not just the globals — test environments
    // stub partial document/window objects.
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      this._onVisibility = () => {
        if (document.visibilityState === 'hidden') {
          this._flushBeacon();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);
    }
    // pagehide is the more reliable pre-unload signal (Safari/iOS may tear
    // down before visibilitychange:hidden on fast reloads). A double fire is
    // harmless: the second call sees an empty queue and returns.
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      this._onPageHide = () => this._flushBeacon();
      window.addEventListener('pagehide', this._onPageHide);
    }
  }

  /** Tear down the interval timer and lifecycle listeners. */
  destroy(): void {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
    if (
      this._onVisibility !== null &&
      typeof document !== 'undefined' &&
      typeof document.removeEventListener === 'function'
    ) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
    if (
      this._onPageHide !== null &&
      typeof window !== 'undefined' &&
      typeof window.removeEventListener === 'function'
    ) {
      window.removeEventListener('pagehide', this._onPageHide);
      this._onPageHide = null;
    }
  }
}

/** Create a production CollectorEmitter. */
export function createCollectorEmitter(options: CollectorEmitterOptions = {}): CollectorEmitter {
  return new CollectorEmitter(options);
}
